export interface LatexSegment {
  value: string;
  isMath: boolean;
}

type Range = [start: number, end: number];

const BACKSLASH = '\\';
const GROUP_CLOSERS: Record<string, string> = { '{': '}', '[': ']' };
const SCRIPT_MARKERS = new Set(['^', '_']);
const SPACING_COMMANDS = new Set([',', ':', ';', '!']);
const CONNECTORS = new Set([
  ' ',
  '\t',
  '+',
  '-',
  '=',
  '<',
  '>',
  '/',
  '*',
  ':',
  '|',
  '(',
  ')',
  '·',
  '±',
]);
const BEGIN_PREFIX = '\\begin{';
const END_PREFIX = '\\end{';

const isLetter = (char: string | undefined): boolean =>
  !!char && /^[A-Za-z]$/.test(char);

const isDigit = (char: string | undefined): boolean =>
  !!char && /^[0-9]$/.test(char);

const isAlphanumeric = (char: string | undefined): boolean =>
  isLetter(char) || isDigit(char);

const findGroupEnd = (source: string, start: number): number => {
  const opener = source[start];
  const closer = GROUP_CLOSERS[opener];
  let depth = 0;
  let index = start;

  while (index < source.length) {
    if (source[index] === opener) {
      depth++;
    }

    if (source[index] === closer) {
      depth--;
    }

    index++;

    if (depth === 0) {
      return index;
    }
  }

  return index;
};

const findScriptArgumentEnd = (source: string, start: number): number => {
  if (start >= source.length) {
    return start;
  }

  const char = source[start];

  if (char in GROUP_CLOSERS) {
    return findGroupEnd(source, start);
  }

  if (char !== BACKSLASH) {
    return start + 1;
  }

  let index = start + 1;

  while (isLetter(source[index])) {
    index++;
  }

  return index;
};

const findScriptsEnd = (source: string, start: number): number => {
  let index = start;

  while (index < source.length) {
    const char = source[index];

    if (isAlphanumeric(char)) {
      index++;
      continue;
    }

    if (!SCRIPT_MARKERS.has(char)) {
      return index;
    }

    index = findScriptArgumentEnd(source, index + 1);
  }

  return index;
};

const findEnvironmentEnd = (source: string, index: number): number | null => {
  if (!source.startsWith(BEGIN_PREFIX, index)) {
    return null;
  }

  const nameStart = index + BEGIN_PREFIX.length;
  const nameEnd = source.indexOf('}', nameStart);

  if (nameEnd === -1) {
    return null;
  }

  const closing = `${END_PREFIX}${source.slice(nameStart, nameEnd)}}`;
  const closingStart = source.indexOf(closing, nameEnd);

  return closingStart === -1 ? null : closingStart + closing.length;
};

const findCommandAnchor = (source: string, index: number): Range | null => {
  const environmentEnd = findEnvironmentEnd(source, index);

  if (environmentEnd !== null) {
    return [index, environmentEnd];
  }

  let end = index + 1;

  while (isLetter(source[end])) {
    end++;
  }

  if (end === index + 1) {
    if (!SPACING_COMMANDS.has(source[end])) {
      return null;
    }

    return [index, findScriptsEnd(source, end + 1)];
  }

  while (source[end] in GROUP_CLOSERS) {
    end = findGroupEnd(source, end);
  }

  return [index, findScriptsEnd(source, end)];
};

const findParenthesisStart = (source: string, closing: number): number => {
  let depth = 0;

  for (let index = closing; index >= 0; index--) {
    if (source[index] === ')') {
      depth++;
    }

    if (source[index] === '(') {
      depth--;
    }

    if (depth === 0) {
      return index;
    }
  }

  return closing + 1;
};

const findScriptBaseStart = (source: string, index: number): number => {
  if (source[index - 1] === ')') {
    return findParenthesisStart(source, index - 1);
  }

  let start = index;

  while (start > 0 && isAlphanumeric(source[start - 1])) {
    start--;
  }

  return start;
};

const findScriptAnchor = (source: string, index: number): Range | null => {
  const start = findScriptBaseStart(source, index);

  if (start === index) {
    return null;
  }

  return [start, findScriptsEnd(source, index)];
};

const findAnchorAt = (source: string, index: number): Range | null => {
  const char = source[index];

  if (char === BACKSLASH) {
    return findCommandAnchor(source, index);
  }

  if (SCRIPT_MARKERS.has(char)) {
    return findScriptAnchor(source, index);
  }

  return null;
};

const findAnchors = (source: string): Range[] => {
  const anchors: Range[] = [];
  let index = 0;

  while (index < source.length) {
    const anchor = findAnchorAt(source, index);

    if (!anchor) {
      index++;
      continue;
    }

    anchors.push(anchor);
    index = anchor[1];
  }

  return anchors;
};

const isConnectedByOperators = (
  source: string,
  start: number,
  end: number,
): boolean =>
  [...source.slice(start, end)].every(
    (char) => CONNECTORS.has(char) || isDigit(char),
  );

const mergeAnchorsIntoRuns = (source: string, anchors: Range[]): Range[] =>
  anchors.reduce<Range[]>((runs, anchor) => {
    const lastRun = runs.at(-1);

    if (lastRun && isConnectedByOperators(source, lastRun[1], anchor[0])) {
      lastRun[1] = anchor[1];
    } else {
      runs.push([...anchor]);
    }

    return runs;
  }, []);

export const splitLatex = (source: string): LatexSegment[] => {
  const segments: LatexSegment[] = [];
  let position = 0;

  for (const [start, end] of mergeAnchorsIntoRuns(
    source,
    findAnchors(source),
  )) {
    if (start > position) {
      segments.push({ value: source.slice(position, start), isMath: false });
    }

    segments.push({ value: source.slice(start, end), isMath: true });
    position = end;
  }

  if (position < source.length) {
    segments.push({ value: source.slice(position), isMath: false });
  }

  return segments;
};
